import React, { useEffect, useState } from 'react';
import { styles, stylesVars } from './HeroBannerCarousel.css';
import { HeroBanner, HeroBannerProps } from '../HeroBanner/HeroBanner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper as SwiperType } from 'swiper/types';
import 'swiper/swiper-bundle.min.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

type HeroBannerItemProps = HeroBannerProps & {
  variant?: 'homepage' | 'collection';
  theme?: {
    background?: React.CSSProperties['color'];
    paginationActive?: React.CSSProperties['color'];
    paginationInactive?: React.CSSProperties['color'];
    arrow?: React.CSSProperties['color'];
  };
};

const LOADING_BANNERS: Array<HeroBannerItemProps> = Array.from(Array(2)).fill({
  isLoading: true,
});

export type HeroBannerCarouselProps = {
  variant?: 'homepage' | 'collection';
  heroBanners: Array<HeroBannerItemProps>;
  isLoading?: boolean;
  shouldAutoPlay?: boolean;
};

export const HeroBannerCarousel = ({
  variant = 'homepage',
  heroBanners: heroBannersProp,
  shouldAutoPlay = true,
  isLoading = !heroBannersProp?.length,
}: HeroBannerCarouselProps) => {
  const [swiper, setSwiper] = useState<SwiperType>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const isMobile = !useBreakpoint('md', 'up');
  const heroBanners = isLoading ? LOADING_BANNERS : heroBannersProp;
  const hasSingleBanner = heroBanners.length === 1;

  const getTheme = (): { theme: HeroBannerItemProps['theme']; navigation: boolean } => {
    if (isLoading) {
      return { theme: undefined, navigation: !hasSingleBanner && !isMobile };
    }

    const activeBanner = heroBanners[activeSlide];
    if (!activeBanner?.isContained && isMobile) {
      return {
        theme: { background: palette.secondary.main, paginationActive: palette.primary.main },
        navigation: !isMobile,
      };
    }

    return {
      theme: activeBanner?.theme,
      navigation: !isMobile,
    };
  };

  const { theme, navigation } = getTheme();

  useEffect(() => {
    swiper?.update();
  }, [swiper, isMobile, isLoading, hasSingleBanner]);

  return (
    <Swiper
      data-testid={`${variant}-hero-banner-carousel`}
      onSlideChange={swiper => setActiveSlide(swiper.realIndex)}
      className={styles.carousel({ variant })}
      modules={[Autoplay, Navigation, Pagination]}
      pagination={{ clickable: true }}
      navigation={navigation}
      autoplay={shouldAutoPlay ? { delay: 5000, disableOnInteraction: false } : undefined}
      loop={!hasSingleBanner}
      onSwiper={newSwiper => setSwiper(newSwiper)}
      style={{
        zIndex: 0,
        ...assignInlineVars({
          [stylesVars.paginationActiveColor]: theme?.paginationActive ?? palette.primary.dark,
          [stylesVars.paginationInactiveColor]: theme?.paginationInactive ?? palette.border.main,
          [stylesVars.arrowColor]: theme?.arrow ?? palette.primary.dark,
        }),
      }}
    >
      {heroBanners.map((heroBanner, i) => (
        <SwiperSlide key={i} className={styles.slide}>
          <HeroBannerSlide
            {...heroBanner}
            theme={!heroBanner.isContained && isMobile ? theme : heroBanner.theme}
            variant={variant}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const HeroBannerSlide = ({ theme, variant, ...heroBannerProps }: HeroBannerItemProps) => {
  return (
    <div
      className={styles.bannerWrapper({ isContained: heroBannerProps.isContained, variant })}
      style={{
        backgroundColor: variant === 'collection' ? palette.secondary.main : theme?.background ?? 'transparent',
      }}
    >
      <HeroBanner {...heroBannerProps} variant={variant} />
    </div>
  );
};
